import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import api from '../lib/api';
import { featureKeys, type EnterpriseFeatureRecord } from './use-enterprise-features';
import { usePermissionsStore, type UserPermissions } from '../stores/permissions-store';
import { useAuthStore, type User } from '../stores/auth-store';

/**
 * A bootstrap failure that a redirect-to-login resolves, NOT a transient one:
 *   • 401 — the axios interceptor already fires AUTH_EXPIRED.
 *   • 404 NOT_FOUND — the token names a user that no longer exists (local
 *     reseed / DATABASE_URL switch / deleted user); AppBootstrap redirects.
 * Retrying either just replays the same dead token, so it's excluded from the
 * transient-retry budget below.
 */
export function isAuthInvalidError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === 401) return true;
  const code = (error.response?.data as { error?: { code?: string } } | undefined)?.error
    ?.code;
  return status === 404 && code === 'NOT_FOUND';
}

/** Max transient retries before the recoverable boundary shows (AppBootstrap
 *  then keeps refetching on its own). */
export const BOOTSTRAP_MAX_RETRIES = 6;

/**
 * React Query `retry` decision for `/bootstrap`. An invalid session must NOT be
 * retried — it redirects to login — while a transient cold-start failure
 * (connection refused / 5xx / timeout while the API warms up) gets a bounded
 * budget of attempts. Exported pure so the policy is unit-testable.
 */
export function shouldRetryBootstrap(failureCount: number, error: unknown): boolean {
  if (isAuthInvalidError(error)) return false;
  return failureCount < BOOTSTRAP_MAX_RETRIES;
}

/**
 * #1429 — Single cold-load request that hydrates the whole authenticated shell.
 *
 * Fetches GET /api/v1/bootstrap and **synchronously seeds** the individual
 * React Query caches it covers, so the standalone hooks (`useMe`,
 * `useEnterpriseFeatures`, `useMyPermissions`, `useCompanyInfo`,
 * `useCompanyPreferences`, `useDashboardPermissions`) become cache hits and
 * do NOT fire their own requests. Seeding happens inside `queryFn` (before the
 * query resolves) so there is no mount race when the shell renders.
 */
interface BootstrapResponse {
  me: { user: User; memberships: unknown[] };
  features: EnterpriseFeatureRecord[];
  permissions: { userId: string; role: string; resolvedPermissions: UserPermissions };
  companyInfo: unknown;
  companyPreferences: unknown;
  dashboardPermissions: unknown;
}

export function useBootstrap() {
  const queryClient = useQueryClient();
  const setPermissions = usePermissionsStore((s) => s.setPermissions);
  const setUser = useAuthStore((s) => s.setUser);
  const setIsLoading = useAuthStore((s) => s.setIsLoading);

  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: async () => {
      const res = await api.get<{ data: BootstrapResponse }>('/bootstrap');
      const d = res.data.data;

      // Seed each downstream cache with the EXACT shape its own endpoint
      // returns, so consumers are cache hits within their staleTime windows.
      queryClient.setQueryData(['auth', 'me'], d.me.user);
      // #1439: also seed the memberships cache so useMyMemberships (org switcher)
      // is a cache hit instead of firing its own /auth/me.
      queryClient.setQueryData(['auth', 'me', 'memberships'], d.me.memberships);
      queryClient.setQueryData(featureKeys.myOrg(), d.features);
      queryClient.setQueryData(['my-permissions'], d.permissions);
      queryClient.setQueryData(['settings', 'company-info'], d.companyInfo);
      queryClient.setQueryData(['settings', 'company-preferences'], d.companyPreferences);
      queryClient.setQueryData(['dashboard-permissions'], d.dashboardPermissions);

      // #1439: sync the org-bearing user into the auth store. This restores the
      // sync that useMe() used to do on boot (replaced by /bootstrap in #1431).
      // The auth store's `user.organization` is what the header OrgSwitcher reads
      // for `currentOrgName`; without this it renders null (super-admin "Switch
      // Organization" + the current-org name vanish on cold load). Login only
      // sets organizationId, so this is the only place organization is populated.
      setUser(d.me.user);
      setIsLoading(false);

      // Prime the permissions store immediately so the shell nav doesn't flash
      // un-gated while useMyPermissions' effect catches up off the seeded cache.
      if (d.permissions?.resolvedPermissions) {
        setPermissions(d.permissions.resolvedPermissions);
      }

      return d;
    },
    staleTime: 1000 * 60 * 5,
    // Cold-start resilience: `/bootstrap` is the very first authenticated call,
    // so right after a server (re)start it can hit an API that is still warming
    // up — connection refused, a 502/503 from the proxy, or a timeout. The old
    // `retry: 1` gave just two attempts ~1s apart, so a server that needed a few
    // seconds to come up left the operator stranded on "We couldn't load your
    // account" until they clicked retry — every cold load. Retry transient
    // failures with capped exponential backoff instead (≈1+2+4+8+8+8s ≈ 31s of
    // coverage), while NOT retrying an invalid session (that must redirect).
    retry: shouldRetryBootstrap,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}
